import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, QrCode, CheckCircle2, RefreshCcw, ImageDown, Clock, PhoneOff, Mail, Check, PlayCircle, X, Volume2, VolumeX,
} from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { downloadReceiptPNG } from "../lib/receipt";
import { TrustBar } from "../components/TrustBar";
import { GiantKeypad } from "../components/GiantKeypad";
import { WizardProgressDots } from "../components/WizardProgressDots";
import { SbpMark } from "../components/SbpMark";
import { HowToAnimation } from "../components/HowToAnimation";
import { Dialog, DialogContent } from "../components/ui/dialog";
import {
  IlluTopUp, IlluPhone, IlluMoney, IlluBankApp, IlluScanQR, IlluPaid,
  IlluSuccess, IlluFail, IlluReceipt,
} from "../components/illustrations";

const screenVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.18 } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.12 } },
};

const fmtAmount = (v) => new Intl.NumberFormat("ru-RU").format(parseInt(v || "0", 10));

export default function Wizard() {
  const { t, lang } = useLang();
  const [step, setStep] = useState("welcome"); // welcome | phone | amount | qr | result
  const [phone, setPhone] = useState(""); // 10 digits after +7
  const [amount, setAmount] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [payment, setPayment] = useState(null);
  const [result, setResult] = useState(null); // paid | failed | expired | cancelled | error
  const [creating, setCreating] = useState(false);
  const [config, setConfig] = useState(null);
  const [rateInfo, setRateInfo] = useState(null);
  const [showHowTo, setShowHowTo] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const audioRef = useRef(null);

  // auto-show the wordless tutorial once, on the client's first visit
  useEffect(() => {
    if (!localStorage.getItem("nord_howto_seen")) {
      setShowHowTo(true);
      localStorage.setItem("nord_howto_seen", "1");
    }
  }, []);

  // play narration by default when the tutorial opens; if the browser blocks
  // autoplay (first-visit auto-open has no user gesture yet), start it on the
  // very first screen touch/tap so for the client it effectively plays by itself.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return undefined;
    if (!showHowTo) {
      a.pause();
      a.currentTime = 0;
      setVoiceOn(false);
      return undefined;
    }
    const tryPlay = () => {
      a.currentTime = 0;
      return a.play().then(() => setVoiceOn(true)).catch(() => setVoiceOn(false));
    };
    tryPlay();
    const onGesture = () => {
      if (a.paused) {
        a.currentTime = 0;
        a.play().then(() => setVoiceOn(true)).catch(() => {});
      }
      cleanup();
    };
    const events = ["pointerdown", "touchstart", "click", "keydown"];
    const cleanup = () => events.forEach((ev) => document.removeEventListener(ev, onGesture, true));
    events.forEach((ev) => document.addEventListener(ev, onGesture, true));
    return cleanup;
  }, [showHowTo, lang]);

  const toggleVoice = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.currentTime = 0;
      a.play().then(() => setVoiceOn(true)).catch(() => {});
    } else {
      a.pause();
      setVoiceOn(false);
    }
  };

  useEffect(() => {
    if (step === "amount" && !rateInfo) {
      api.get("/rate").then((r) => setRateInfo(r.data)).catch(() => {});
    }
  }, [step, rateInfo]);
  const [slowHint, setSlowHint] = useState(false);
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [phoneError, setPhoneError] = useState(null); // 'not_found' | 'network'
  const clientKeyRef = useRef(null);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  // ---- payment status polling ----
  useEffect(() => {
    if (step !== "qr" || !payment) return undefined;
    const iv = setInterval(async () => {
      try {
        const res = await api.get(`/payments/${payment.id}`);
        const st = res.data.status;
        if (st === "paid") {
          setPayment(res.data);
          setResult("paid");
          setStep("result");
        } else if (["failed", "expired", "cancelled"].includes(st)) {
          setPayment(res.data);
          setResult(st);
          setStep("result");
        }
      } catch (e) {
        /* keep polling */
      }
    }, 2000);
    return () => clearInterval(iv);
  }, [step, payment]);

  // ---- "bank is slow" hint after 90s on QR screen ----
  useEffect(() => {
    if (step !== "qr" || !payment) {
      setSlowHint(false);
      return undefined;
    }
    const tm = setTimeout(() => setSlowHint(true), 90000);
    return () => clearTimeout(tm);
  }, [step, payment]);

  // clear the phone error whenever digits change
  useEffect(() => {
    setPhoneError(null);
  }, [phone]);

  const checkPhoneAndNext = useCallback(async () => {
    if (phoneChecking) return;
    setPhoneChecking(true);
    setPhoneError(null);
    try {
      const res = await api.post("/phone-check", { phone: `7${phone}` });
      if (res.data.allowed) {
        setStep("amount");
      } else {
        setPhoneError("not_found");
      }
    } catch (e) {
      setPhoneError("network");
    } finally {
      setPhoneChecking(false);
    }
  }, [phone, phoneChecking]);

  const createPayment = useCallback(async () => {
    if (creating) return;
    setCreating(true);
    try {
      clientKeyRef.current =
        (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
        `ck-${Date.now()}-${Math.random()}`;
      const res = await api.post("/payments", {
        phone: `7${phone}`,
        amount: parseInt(amount, 10),
        client_key: clientKeyRef.current,
        lang,
      });
      setPayment(res.data);
      setStep("qr");
    } catch (e) {
      setResult("error");
      setStep("result");
    } finally {
      setCreating(false);
    }
  }, [phone, amount, lang, creating]);

  const cancelAndBack = useCallback(async () => {
    const p = payment;
    setPayment(null);
    setStep("amount");
    if (p) {
      try {
        await api.post(`/payments/${p.id}/cancel`);
      } catch (e) {
        /* ignore */
      }
    }
  }, [payment]);

  const resetAll = () => {
    setPhone("");
    setAmount("");
    setPayment(null);
    setResult(null);
    setAgreed(false);
    setStep("welcome");
  };

  const retry = () => {
    setPayment(null);
    setResult(null);
    setStep("amount");
  };

  const saveReceipt = async () => {
    if (!payment) return;
    const ok = await downloadReceiptPNG({ payment, lang, mockMode: config?.mock_mode });
    if (ok) toast.success(t.receipt_saved);
  };

  // ---- input handlers ----
  const phoneDigit = (d) =>
    setPhone((p) => {
      // client habit: numbers dialed as 8XXXXXXXXXX — the leading 8 is the
      // trunk prefix, +7 is already shown, so ignore a first-pressed 8
      if (p === "" && d === "8") return p;
      return p.length >= 10 ? p : p + d;
    });
  const amountDigit = (d) =>
    setAmount((a) => {
      if (a === "" && d === "0") return a;
      if (a.length >= 7) return a;
      return a + d;
    });

  const phoneValid = phone.length === 10;
  const amountValid = amount !== "" && parseInt(amount, 10) >= 10 && parseInt(amount, 10) <= 1000000;

  // ---- phone display: +7 XXX XXX-XX-XX with placeholders ----
  const renderPhoneDisplay = () => {
    const chars = [];
    const groups = [3, 3, 2, 2];
    let di = 0;
    chars.push({ c: "+7", filled: true, sep: true });
    groups.forEach((g, gi) => {
      chars.push({ c: " ", sep: true });
      if (gi >= 2) chars[chars.length - 1].c = "-";
      for (let k = 0; k < g; k++) {
        if (di < phone.length) chars.push({ c: phone[di], filled: true });
        else chars.push({ c: "\u2022", filled: false });
        di++;
      }
    });
    return (
      <div
        data-testid="phone-display"
        className="font-display tabular-nums text-4xl sm:text-5xl font-semibold tracking-tight text-center whitespace-nowrap"
      >
        {chars.map((ch, i) => (
          <span key={i} className={ch.filled || ch.sep ? "text-foreground" : "text-muted-foreground/40"}>
            {ch.c}
          </span>
        ))}
      </div>
    );
  };

  const fmtPhoneFull = (p) =>
    p && p.length === 10 ? `+7 ${p.slice(0, 3)} ${p.slice(3, 6)}-${p.slice(6, 8)}-${p.slice(8, 10)}` : "";

  const BigButton = ({ onClick, disabled, testid, color = "success", children }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={`keypad-key w-full min-h-[68px] rounded-2xl flex items-center justify-center gap-3 font-display text-2xl font-bold text-white shadow-[0_12px_28px_rgba(2,6,23,0.16)] ${
        color === "success"
          ? "bg-[hsl(var(--success))]"
          : color === "primary"
          ? "bg-[hsl(var(--primary))]"
          : "bg-[hsl(var(--destructive))]"
      } ${disabled ? "opacity-35 pointer-events-none" : ""}`}
    >
      {children}
    </button>
  );

  const BackButton = ({ onClick, testid = "wizard-back-button" }) => (
    <button
      data-testid={testid}
      aria-label="Назад / Atrás"
      onClick={onClick}
      className="keypad-key w-14 h-14 rounded-2xl bg-white border border-border shadow-[0_6px_16px_rgba(2,6,23,0.08)] flex items-center justify-center text-foreground"
    >
      <ArrowLeft size={30} strokeWidth={2.5} />
    </button>
  );

  return (
    <div className="min-h-[100svh] bg-background flex flex-col">
      <TrustBar />
      <main className="flex-1 w-full max-w-md mx-auto px-4 pt-4 pb-6 flex flex-col">
        <WizardProgressDots current={step} />

        <AnimatePresence mode="wait">
          {/* ============ WELCOME ============ */}
          {step === "welcome" && (
            <motion.div key="welcome" {...screenVariants} className="flex-1 flex flex-col justify-center gap-6 mt-4">
              <div
                className="rounded-3xl bg-white border border-border shadow-[0_18px_50px_rgba(2,6,23,0.10)] p-6 sm:p-8 flex flex-col items-center gap-4"
                style={{
                  backgroundImage:
                    "linear-gradient(180deg, rgba(30,91,255,0.05), rgba(0,195,137,0.04) 40%, rgba(255,255,255,0) 70%)",
                }}
              >
                <IlluTopUp className="w-56 sm:w-64 h-auto" />
                <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-center">
                  {t.welcome_title}
                </h1>
                <p className="text-muted-foreground text-lg text-center">{t.brand} • {t.welcome_sub}</p>
                <div className="flex items-center gap-2 bg-muted rounded-full px-4 py-2">
                  <SbpMark size={22} />
                  <span className="font-semibold text-sm">{t.sbp}</span>
                </div>
              </div>

              {/* wordless looping tutorial (visible on start) */}
              <HowToAnimation compact />

              <button
                type="button"
                data-testid="howto-open-button"
                onClick={() => setShowHowTo(true)}
                className="mx-auto flex items-center gap-2 rounded-full border-2 border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 px-5 py-2.5 text-[hsl(var(--primary))] font-semibold transition-colors hover:bg-[hsl(var(--primary))]/12"
              >
                <PlayCircle size={24} strokeWidth={2.4} />
                {t.how_it_works}
              </button>

              <BigButton testid="start-topup-button" onClick={() => setStep("phone")}>
                {t.start}
                <ArrowRight size={30} strokeWidth={2.8} />
              </BigButton>
            </motion.div>
          )}

          {/* ============ PHONE ============ */}
          {step === "phone" && (
            <motion.div key="phone" {...screenVariants} className="flex-1 flex flex-col gap-3 mt-3">
              <div className="flex items-center gap-3">
                <BackButton onClick={() => setStep("welcome")} />
                <span className="text-lg font-semibold text-muted-foreground">{t.phone_title}</span>
              </div>
              <div className="rounded-3xl bg-white border border-border shadow-[0_10px_30px_rgba(15,23,42,0.08)] px-3 pt-2 pb-5 flex flex-col items-center gap-1">
                <IlluPhone className="w-44 sm:w-48 h-auto" />
                {renderPhoneDisplay()}
              </div>
              {phoneError && (
                <div
                  data-testid="phone-not-allowed"
                  className="result-shake flex items-center gap-3 rounded-2xl border-2 border-[hsl(var(--destructive))]/50 bg-[hsl(var(--destructive))]/10 px-4 py-3"
                >
                  <PhoneOff size={28} strokeWidth={2.4} className="text-[hsl(var(--destructive))] shrink-0" />
                  <span className="font-semibold text-[hsl(var(--destructive))]">
                    {phoneError === "network" ? t.error_net : t.phone_not_found}
                  </span>
                </div>
              )}
              <div className="mt-auto flex flex-col gap-3">
                <GiantKeypad onDigit={phoneDigit} onBackspace={() => setPhone((p) => p.slice(0, -1))} onClear={() => setPhone("")} />
                <BigButton testid="wizard-next-button" onClick={checkPhoneAndNext} disabled={!phoneValid || phoneChecking}>
                  {phoneChecking ? (
                    <span className="flex gap-2 items-center">
                      <span className="wait-dot w-3 h-3 rounded-full bg-white inline-block" />
                      <span className="wait-dot w-3 h-3 rounded-full bg-white inline-block" />
                      <span className="wait-dot w-3 h-3 rounded-full bg-white inline-block" />
                    </span>
                  ) : (
                    <>
                      {t.next}
                      <ArrowRight size={28} strokeWidth={2.8} />
                    </>
                  )}
                </BigButton>
              </div>
            </motion.div>
          )}

          {/* ============ AMOUNT ============ */}
          {step === "amount" && (
            <motion.div key="amount" {...screenVariants} className="flex-1 flex flex-col gap-3 mt-3">
              <div className="flex items-center gap-3">
                <BackButton onClick={() => setStep("phone")} />
                <span className="text-lg font-semibold text-muted-foreground">{t.amount_title}</span>
              </div>
              <div className="rounded-3xl bg-white border border-border shadow-[0_10px_30px_rgba(15,23,42,0.08)] px-3 pt-2 pb-5 flex flex-col items-center gap-1">
                <IlluMoney className="w-44 sm:w-48 h-auto" />
                <div className="flex items-center justify-center gap-2">
                  <div
                    data-testid="amount-display"
                    className={`font-display tabular-nums text-5xl sm:text-6xl font-semibold tracking-tight ${
                      amount === "" ? "text-muted-foreground/40" : "text-foreground"
                    }`}
                  >
                    {fmtAmount(amount)}
                  </div>
                  <span className="font-display text-4xl sm:text-5xl font-semibold text-[hsl(var(--accent))]">₽</span>
                  <span className="caret-blink w-[3px] h-12 bg-[hsl(var(--primary))] rounded-full" />
                </div>
                {rateInfo?.rate_client && (
                  <div data-testid="rate-info" className="text-sm text-muted-foreground">
                    {t.rate_lbl}: 1 USDT ≈ {new Intl.NumberFormat("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(rateInfo.rate_client)} ₽
                  </div>
                )}
              </div>
              <div className="mt-auto flex flex-col gap-3">
                <GiantKeypad onDigit={amountDigit} onBackspace={() => setAmount((a) => a.slice(0, -1))} onClear={() => setAmount("")} />
                <div className="flex items-start gap-3 px-1">
                  <button
                    type="button"
                    data-testid="terms-agree-checkbox"
                    aria-checked={agreed}
                    role="checkbox"
                    onClick={() => setAgreed((v) => !v)}
                    className={`shrink-0 w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-colors ${
                      agreed
                        ? "bg-[hsl(var(--primary))] border-[hsl(var(--primary))] text-white"
                        : "bg-white border-border text-transparent"
                    }`}
                  >
                    <Check size={22} strokeWidth={3.2} />
                  </button>
                  <span className="text-sm leading-snug text-muted-foreground pt-1">
                    {t.terms_agree}{" "}
                    <a
                      data-testid="terms-link"
                      href="/docs/velowallet_legal.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-[hsl(var(--primary))] font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t.terms_link}
                    </a>
                  </span>
                </div>
                <BigButton testid="wizard-show-qr-button" onClick={createPayment} disabled={!amountValid || !agreed || creating}>
                  {creating ? (
                    <span className="flex gap-2 items-center">
                      <span className="wait-dot w-3 h-3 rounded-full bg-white inline-block" />
                      <span className="wait-dot w-3 h-3 rounded-full bg-white inline-block" />
                      <span className="wait-dot w-3 h-3 rounded-full bg-white inline-block" />
                    </span>
                  ) : (
                    <>
                      <QrCode size={28} strokeWidth={2.6} />
                      {t.show_qr}
                    </>
                  )}
                </BigButton>
              </div>
            </motion.div>
          )}

          {/* ============ QR + WAIT ============ */}
          {step === "qr" && payment && (
            <motion.div key="qr" {...screenVariants} className="flex-1 flex flex-col gap-4 mt-4 items-center">
              <div className="w-full flex items-center gap-3">
                <BackButton onClick={cancelAndBack} />
                <div className="flex items-center gap-2 text-muted-foreground">
                  <SbpMark size={24} />
                  <span className="text-lg font-semibold">{t.scan_title}</span>
                </div>
                {config?.mock_mode && (
                  <span className="ml-auto text-[10px] font-bold px-2 py-1 rounded-full bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))]">
                    {t.demo_note}
                  </span>
                )}
              </div>

              <div
                data-testid="sbp-qr-card"
                className="relative rounded-3xl bg-white border-2 border-border p-5 shadow-[0_18px_50px_rgba(2,6,23,0.10)]"
                style={{ boxShadow: "0 18px 50px rgba(2,6,23,0.10), 0 0 80px rgba(0,183,255,0.10)" }}
              >
                <QRCodeSVG
                  data-testid="sbp-qr-image"
                  value={payment.qr_payload || ""}
                  size={248}
                  level="M"
                  marginSize={2}
                />
                <div className="qr-scanline" />
              </div>

              <div className="font-display tabular-nums text-3xl font-bold">
                {fmtAmount(payment.amount)} <span className="text-[hsl(var(--accent))]">₽</span>
              </div>

              {/* direct pay button for mobile users (real provider payment page) */}
              {payment.payment_url && (
                <>
                  <BigButton
                    testid="open-payment-button"
                    onClick={() => window.open(payment.payment_url, "_blank", "noopener")}
                  >
                    <SbpMark size={26} />
                    {t.pay_now}
                    <ArrowRight size={28} strokeWidth={2.8} />
                  </BigButton>
                  <span className="text-xs text-muted-foreground -mt-2">{t.or_scan}</span>
                </>
              )}

              {/* child-book pictogram instructions: 1 bank app -> 2 scan QR -> 3 paid */}
              <div data-testid="qr-instruction-strip" className="w-full grid grid-cols-3 gap-2">
                {[
                  { Illu: IlluBankApp, label: t.open_bank },
                  { Illu: IlluScanQR, label: t.camera },
                  { Illu: IlluPaid, label: t.scan },
                ].map(({ Illu, label }, i) => (
                  <div key={i} className="relative rounded-2xl bg-white border border-border pt-2 pb-2 px-1 flex flex-col items-center gap-1">
                    <span className="absolute -top-2.5 -left-1.5 w-7 h-7 rounded-full bg-[hsl(var(--primary))] text-white font-display font-bold text-base flex items-center justify-center shadow">
                      {i + 1}
                    </span>
                    <Illu className="w-full max-w-[92px] h-auto" />
                    <span className="text-[11px] font-semibold text-muted-foreground">{label}</span>
                    {i < 2 && (
                      <ArrowRight size={18} strokeWidth={3.2} className="absolute -right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--primary))] z-10" />
                    )}
                  </div>
                ))}
              </div>

              <div data-testid="payment-waiting" className="flex items-center gap-3 mt-1">
                <span className="wait-dot w-3.5 h-3.5 rounded-full bg-[hsl(var(--primary))] inline-block" />
                <span className="wait-dot w-3.5 h-3.5 rounded-full bg-[hsl(var(--primary))] inline-block" />
                <span className="wait-dot w-3.5 h-3.5 rounded-full bg-[hsl(var(--primary))] inline-block" />
                <span className="text-muted-foreground font-medium">{t.waiting}</span>
              </div>

              {slowHint && (
                <div
                  data-testid="slow-bank-hint"
                  className="flex items-center gap-2 rounded-2xl border border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/15 px-4 py-3"
                >
                  <Clock size={22} strokeWidth={2.4} className="text-[hsl(var(--warning))] shrink-0" />
                  <span className="text-sm font-medium">{t.slow_bank}</span>
                </div>
              )}
            </motion.div>
          )}

          {/* ============ RESULT ============ */}
          {step === "result" && (
            <motion.div key="result" {...screenVariants} data-testid="payment-result" className="flex-1 flex flex-col justify-center gap-5 mt-4">
              {result === "paid" ? (
                <>
                  <div
                    data-testid="payment-success"
                    className="result-pop rounded-3xl bg-white border border-border shadow-[0_18px_50px_rgba(2,6,23,0.10)] p-6 flex flex-col items-center gap-3"
                  >
                    <IlluSuccess className="w-52 sm:w-60 h-auto" />
                    <div className="font-display text-3xl font-bold">{t.success}</div>
                    <div className="font-display tabular-nums text-5xl font-bold text-[hsl(var(--success))]">
                      {fmtAmount(payment?.amount)} ₽
                    </div>
                  </div>

                  {/* receipt card */}
                  <div
                    data-testid="receipt-card"
                    className="rounded-2xl bg-white border-2 border-dashed border-border p-5 flex flex-col gap-3"
                  >
                    <div className="flex items-center gap-3">
                      <IlluReceipt className="w-9 h-auto" />
                      <span className="font-display font-bold text-lg">{t.receipt}</span>
                      <span className="ml-auto"><SbpMark size={20} /></span>
                    </div>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm text-muted-foreground">{t.operation_no}</span>
                      <span data-testid="receipt-operation-number" className="font-display tabular-nums text-3xl font-bold">
                        {payment?.operation_number ?? "—"}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm text-muted-foreground">{t.phone_lbl}</span>
                      <span className="tabular-nums font-semibold">{fmtPhoneFull(phone)}</span>
                    </div>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm text-muted-foreground">{t.date_lbl}</span>
                      <span className="tabular-nums font-semibold">
                        {new Date(payment?.paid_at || Date.now()).toLocaleString(lang === "es" ? "es-ES" : "ru-RU", {
                          day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
                        })}
                      </span>
                    </div>
                  </div>

                  <BigButton testid="save-receipt-button" color="primary" onClick={saveReceipt}>
                    <ImageDown size={28} strokeWidth={2.6} />
                    {t.save_receipt}
                  </BigButton>
                  <BigButton testid="payment-done-button" onClick={resetAll}>
                    <CheckCircle2 size={28} strokeWidth={2.6} />
                    {t.done}
                  </BigButton>
                </>
              ) : (
                <>
                  <div
                    data-testid="payment-failure"
                    className="result-shake rounded-3xl bg-white border border-border shadow-[0_18px_50px_rgba(2,6,23,0.10)] p-8 flex flex-col items-center gap-4"
                  >
                    <IlluFail className="w-56 sm:w-64 h-auto" />
                    <div className="font-display text-4xl font-bold text-center">
                      {result === "expired" ? t.expired : result === "error" ? t.error_net : t.fail}
                    </div>
                  </div>
                  <BigButton testid="payment-retry-button" color="primary" onClick={retry}>
                    <RefreshCcw size={28} strokeWidth={2.6} />
                    {t.retry}
                  </BigButton>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* fullscreen wordless tutorial */}
      <Dialog open={showHowTo} onOpenChange={setShowHowTo}>
        <DialogContent
          data-testid="howto-dialog"
          className="max-w-md p-6 border-0 bg-gradient-to-b from-[hsl(var(--primary))]/8 to-white"
        >
          <div className="flex flex-col items-center gap-4 pt-2">
            <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
              <PlayCircle size={26} strokeWidth={2.4} />
              <span className="font-display font-bold text-xl">{t.how_it_works}</span>
            </div>
            <HowToAnimation />
            <audio ref={audioRef} src={`/audio/howto_${lang === "es" ? "es" : "ru"}.mp3`} preload="auto" onEnded={() => setVoiceOn(false)} />
            <button
              type="button"
              data-testid="howto-voice-button"
              onClick={toggleVoice}
              className={`flex items-center gap-2 rounded-full px-6 py-3 font-semibold transition-colors ${
                voiceOn
                  ? "bg-[hsl(var(--primary))] text-white"
                  : "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] voice-pulse"
              }`}
            >
              {voiceOn ? <Volume2 size={24} strokeWidth={2.4} className="animate-pulse" /> : <VolumeX size={24} strokeWidth={2.4} />}
              {t.listen}
            </button>
            <button
              type="button"
              data-testid="howto-close-button"
              onClick={() => setShowHowTo(false)}
              className="mt-1 flex items-center gap-2 rounded-full bg-[hsl(var(--success))] px-7 py-3 text-white font-semibold shadow-md transition-transform active:scale-95"
            >
              <Check size={22} strokeWidth={3} />
              {t.got_it}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <footer className="w-full py-4 px-6 flex justify-center">
        <p data-testid="footer-id-text" className="text-sm text-muted-foreground text-center max-w-md leading-relaxed">
          {t.footer_id_text}{" "}
          <a
            data-testid="footer-contact-email"
            href="mailto:clients@velowallet.ru"
            className="inline-flex items-center gap-1 underline text-[hsl(var(--primary))] font-medium whitespace-nowrap align-middle"
          >
            <Mail size={14} strokeWidth={2.2} />
            clients@velowallet.ru
          </a>
        </p>
      </footer>
    </div>
  );
}
